import { useEffect, useRef, useState, useCallback } from "react";
import DOMPurify from "dompurify";
import { useCommentDraft } from "@/hooks/useCommentDraft";
import { Link } from "react-router-dom";
import { Bell, BellOff, Calendar, Check, ChevronDown, CornerDownLeft, FileText, Loader2, Lock, MessageSquare, Paperclip, Pencil, Plus, Send, Table2, Trash2, UserPlus, X } from "lucide-react";
import { openPendingFile } from "../../../utils/download";
import { Spinner } from "@/components/ui/Spinner";
import { usePlannedRevenuesBySubtask } from "@/features/finances/hooks/useFinances";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { formatDate, formatDateTime } from "@/utils/formatDate";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import type { MentionSuggestion } from "@/components/ui/RichTextEditor";
import { subtaskCommentsApi } from "@/features/tasks/api/subtask_comments";
import { WeekSelect } from "@/components/ui/WeekSelect";
import {
  useCreateSubTask,
  useDeleteCommentAttachment,
  useDeleteSubTask,
  useDeleteSubTaskAttachment,
  useSubTaskAttachments,
  useSubTasks,
  useUpdateSubTask,
  useUploadCommentAttachment,
  useUploadSubTaskAttachment,
  useWatchSubTask,
} from "@/features/tasks/hooks/useSubTasks";
import {
  useSubTaskSpreadsheetLinks,
  useCreateSubTaskSpreadsheetLink,
  useDeleteSubTaskSpreadsheetLink,
  useRenameSubTaskSpreadsheetLink,
  useUpdateSubTaskSpreadsheetLink,
} from "@/features/tasks/hooks/useSpreadsheetLinks";
import { SpreadsheetLinkList } from "@/features/tasks/components/SpreadsheetLinkList";
import {
  useCreateSubTaskComment,
  useDeleteSubTaskComment,
  useSubTaskComments,
  useUpdateSubTaskComment,
} from "@/features/tasks/hooks/useSubTaskComments";
import { useInternalUsers } from "@/features/tasks/hooks/useInternalUsers";
import { useAuthStore } from "@/store/auth";
import { getRoleLabel } from "@/utils/roleLabel";
import { highlightMentions, highlightMentionsInHtml } from "@/utils/highlightMentions";
import {
  PRIORITY_COLORS,
  PRIORITY_LABELS,
  TASK_STATUS_COLORS,
  TASK_STATUS_LABELS,
  type SubTaskComment,
  type TaskPriority,
  type TaskStatus,
} from "@/features/tasks/types";

const ACCEPTED_EXTENSIONS = [
  // images
  "jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "heic", "heif", "avif", "tiff", "tif", "ico",
  // pdf
  "pdf",
  // spreadsheets
  "csv", "tsv", "xls", "xlsx", "xlsm", "ods",
  // documents
  "doc", "docx", "odt", "rtf", "txt", "md",
  // presentations
  "ppt", "pptx", "odp",
  // archives
  "zip", "rar", "7z", "tar", "gz",
];

const FILE_INPUT_ACCEPT = `image/*,.${ACCEPTED_EXTENSIONS.join(",.")}`;

function isAcceptedFile(f: File): boolean {
  if (f.type.startsWith("image/")) return true;
  const ext = f.name.split(".").pop()?.toLowerCase() ?? "";
  return ACCEPTED_EXTENSIONS.includes(ext);
}

function isHtmlContent(content: string) {
  return content.trimStart().startsWith("<");
}

function stripHtml(html: string): string {
  if (!isHtmlContent(html)) return html;
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function sanitize(html: string) {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "ul", "ol", "li", "img", "h1", "h2", "h3"],
    ALLOWED_ATTR: ["src", "alt", "width", "height", "start"],
  });
}

function CommentContent({ content }: { content: string }) {
  const { data: allUsers = [] } = useInternalUsers({ includeFridayContractors: true });
  const names = allUsers.map((u) => u.full_name);
  if (isHtmlContent(content)) {
    return (
      <div
        className="rich-text-content text-sm text-gray-700 dark:text-gray-300 break-words"
        dangerouslySetInnerHTML={{ __html: highlightMentionsInHtml(sanitize(content), names) }}
      />
    );
  }
  return (
    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
      {highlightMentions(content, names)}
    </p>
  );
}

type CommentNodeType = SubTaskComment & { replies: CommentNodeType[] };

function buildCommentTree(comments: SubTaskComment[]): CommentNodeType[] {
  const map = new Map<string, CommentNodeType>();
  comments.forEach((c) => map.set(c.id, { ...c, replies: [] }));
  const roots: CommentNodeType[] = [];
  map.forEach((node) => {
    if (node.reply_to && map.has(node.reply_to)) {
      map.get(node.reply_to)!.replies.push(node);
    } else {
      roots.push(node);
    }
  });
  return roots;
}

const MAX_DEPTH = 4;

interface CommentNodeProps {
  node: CommentNodeType;
  depth: number;
  currentUserId: string | null;
  taskId: string;
  subtaskId: string;
  editingCommentId: string | null;
  editCommentContent: string;
  editCommentMentionIds: string[];
  mentionSuggestions: MentionSuggestion[];
  onEditStart: (id: string, content: string, mentionIds: string[]) => void;
  onEditCancel: () => void;
  onEditSave: () => void;
  onEditContentChange: (content: string) => void;
  onEditMentionIdsChange: (ids: string[]) => void;
  onDelete: (id: string) => void;
  onReply: (c: { id: string; author: string | null; author_name: string; content: string }) => void;
  onDeleteAttachment: (commentId: string, attId: string) => void;
}

function CommentNode({
  node,
  depth,
  currentUserId,
  taskId,
  subtaskId,
  editingCommentId,
  editCommentContent,
  editCommentMentionIds: _editCommentMentionIds,
  mentionSuggestions,
  onEditStart,
  onEditCancel,
  onEditSave,
  onEditContentChange,
  onEditMentionIdsChange,
  onDelete,
  onReply,
  onDeleteAttachment,
}: CommentNodeProps) {
  const isOwn = currentUserId === node.author;
  const isEditing = editingCommentId === node.id;
  const visualDepth = Math.min(depth, MAX_DEPTH);

  return (
    <div className={visualDepth > 0 ? "pl-3 border-l-2 border-gray-200 dark:border-gray-600" : ""}>
      <div className="flex gap-2.5 group py-1.5">
        <UserAvatar
          userId={node.author}
          fullName={node.author_name}
          profilePictureUrl={node.author_profile_picture_url}
          size="lg"
          className="mt-0.5 flex-shrink-0"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 mb-0.5 flex-wrap">
            <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{node.author_name}</span>
            <span className="text-xs text-gray-400 dark:text-gray-500">
              {new Date(node.created_at).toLocaleString("pl-PL", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
            </span>
            {node.is_edited && <span className="text-xs text-gray-400 dark:text-gray-500 italic">(edytowano)</span>}
          </div>

          {node.reply_to_author_name && !isEditing && (
            <div className="flex items-start gap-1.5 mb-1.5 bg-gray-100 dark:bg-gray-700 border-l-2 border-gray-400 dark:border-gray-500 rounded-r px-2 py-1">
              <CornerDownLeft size={11} className="text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">{node.reply_to_author_name}: </span>
                <span className="text-xs text-gray-400 dark:text-gray-500 italic truncate">{stripHtml(node.reply_to_content ?? "")}</span>
              </div>
            </div>
          )}

          {isEditing ? (
            <div className="flex flex-col gap-2 mt-1">
              <RichTextEditor
                value={editCommentContent}
                onChange={onEditContentChange}
                placeholder="Edytuj komentarz…"
                onImageUpload={(file) => subtaskCommentsApi.uploadCommentImage(taskId, subtaskId, file)}
                mentionSuggestions={mentionSuggestions}
                onMentionIdsChange={onEditMentionIdsChange}
              />
              <div className="flex gap-1">
                <button
                  onClick={onEditSave}
                  title="Zapisz"
                  className="p-2 bg-[var(--color-primary)] text-white rounded-lg hover:bg-[var(--color-primary-hover)] disabled:opacity-40"
                >
                  <Check size={15} />
                </button>
                <button
                  onClick={onEditCancel}
                  title="Anuluj"
                  className="p-2 border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50"
                >
                  <X size={15} />
                </button>
              </div>
            </div>
          ) : (
            <CommentContent content={node.content} />
          )}

          {node.attachments.length > 0 && !isEditing && (
            <div className="mt-1.5 flex flex-col gap-1">
              {node.attachments.map((att) => (
                <div key={att.id} className="flex items-center gap-1.5 group/att">
                  <span className="text-xs">{fileIcon(att.original_name)}</span>
                  <a href={att.url} target="_blank" rel="noopener noreferrer" className="text-xs text-[var(--color-primary)] hover:underline truncate">
                    {att.original_name}
                  </a>
                  <button
                    onClick={() => onDeleteAttachment(node.id, att.id)}
                    className="text-gray-300 hover:text-red-400 opacity-0 group-hover/att:opacity-100 transition-opacity"
                  >
                    <X size={10} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {!isEditing && (
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity mt-0.5 flex-shrink-0">
            <button
              onClick={() => onReply({ id: node.id, author: node.author, author_name: node.author_name, content: node.content })}
              title="Odpowiedz"
              className="text-gray-400 dark:text-gray-500 hover:text-[var(--color-primary)]"
            >
              <CornerDownLeft size={13} />
            </button>
            {isOwn && (
              <>
                <button onClick={() => onEditStart(node.id, node.content, node.mentions.map((m) => m.id))} className="text-gray-400 dark:text-gray-500 hover:text-[var(--color-primary)]">
                  <Pencil size={13} />
                </button>
                <button onClick={() => onDelete(node.id)} className="text-gray-400 dark:text-gray-500 hover:text-red-500">
                  <Trash2 size={13} />
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {node.replies.map((child) => (
        <CommentNode
          key={child.id}
          node={child}
          depth={depth + 1}
          currentUserId={currentUserId}
          taskId={taskId}
          subtaskId={subtaskId}
          editingCommentId={editingCommentId}
          editCommentContent={editCommentContent}
          editCommentMentionIds={_editCommentMentionIds}
          mentionSuggestions={mentionSuggestions}
          onEditStart={onEditStart}
          onEditCancel={onEditCancel}
          onEditSave={onEditSave}
          onEditContentChange={onEditContentChange}
          onEditMentionIdsChange={onEditMentionIdsChange}
          onDelete={onDelete}
          onReply={onReply}
          onDeleteAttachment={onDeleteAttachment}
        />
      ))}
    </div>
  );
}

interface Props {
  taskId: string;
  subtaskId: string;
  onClose: () => void;
}

const STATUS_ORDER: TaskStatus[] = ["todo_later", "todo_now", "in_progress", "waiting", "done", "cancelled", "n_d"];

function WeekBadge({ weeks }: { weeks: number | null }) {
  if (weeks === null) return null;
  const text =
    weeks === 0 ? "Ten tydzień" : weeks > 0 ? `+${weeks} tyg.` : `${weeks} tyg.`;
  const cls =
    weeks < -1 ? "bg-red-100 text-red-700 font-bold"
    : weeks === -1 ? "bg-red-50 text-red-500 font-semibold"
    : weeks === 0 ? "bg-orange-100 text-orange-600 font-bold"
    : weeks === 1 ? "bg-yellow-100 text-yellow-700 font-semibold"
    : "bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400";
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm ${cls}`}>
      {text}
    </span>
  );
}

function fileIcon(name: string) {
  const ext = name.split(".").pop()?.toLowerCase() ?? "";
  if (["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "heic", "heif", "avif", "tiff", "tif", "ico"].includes(ext)) return "🖼️";
  if (ext === "pdf") return "📄";
  if (["xls", "xlsx", "xlsm", "csv", "tsv", "ods"].includes(ext)) return "📊";
  if (["doc", "docx", "odt", "rtf", "txt", "md"].includes(ext)) return "📝";
  if (["ppt", "pptx", "odp"].includes(ext)) return "📋";
  if (["zip", "rar", "7z", "tar", "gz"].includes(ext)) return "🗜️";
  return "📎";
}


export function SubTaskDetailPanel({ taskId, subtaskId, onClose }: Props) {
  const { data: subtasks = [], isLoading } = useSubTasks(taskId);
  const subtask = subtasks.find((s) => s.id === subtaskId);

  const { mutate: updateSubTask } = useUpdateSubTask(taskId);
  const { mutate: deleteSubTask } = useDeleteSubTask(taskId);
  const { data: comments = [] } = useSubTaskComments(taskId, subtaskId);
  const { mutate: createComment, isPending: sending } = useCreateSubTaskComment(taskId, subtaskId);
  const { mutate: updateComment } = useUpdateSubTaskComment(taskId, subtaskId);
  const { mutate: deleteComment } = useDeleteSubTaskComment(taskId, subtaskId);
  const { data: attachments = [] } = useSubTaskAttachments(taskId, subtaskId);
  const { mutate: uploadAttachment, isPending: uploading } = useUploadSubTaskAttachment(taskId, subtaskId);
  const { mutate: deleteAttachment } = useDeleteSubTaskAttachment(taskId, subtaskId);
  const { mutate: uploadCommentAttachment } = useUploadCommentAttachment(taskId, subtaskId);
  const { mutate: deleteCommentAttachmentMutate } = useDeleteCommentAttachment(taskId, subtaskId);
  const { data: allUsers = [] } = useInternalUsers({ includeFridayContractors: true });
  // Assignment is branch-scoped; mentions above stay company-wide on `allUsers`.
  const { data: assignableUsers = [] } = useInternalUsers({
    includeFridayContractors: true,
    sameBranch: true,
  });
  const currentUser = useAuthStore((s) => s.user);
  const { mutate: watchSubTask, isPending: watchingSubTask } = useWatchSubTask(taskId, subtaskId);

  const isDirector = currentUser?.role === "director";
  const canViewInvoicePrz =
    currentUser?.role === "accountant" ||
    (currentUser?.role === "director" && (!!currentUser.is_technical_director || !!currentUser.is_ceo));
  const canUnassign =
    currentUser?.is_ceo ||
    currentUser?.role === "director" ||
    (currentUser?.role === "coordinator" &&
      subtask?.assignees.some((a) => a.id === currentUser?.id));
  const { data: linkedRevenues = [] } = usePlannedRevenuesBySubtask(isDirector ? subtaskId : undefined);

  const { mutate: createChildSubTask, isPending: creatingChild } = useCreateSubTask(taskId);
  const children = (subtasks ?? [])
    .filter((s) => s.parent === subtaskId)
    .sort((a, b) => a.position - b.position || a.created_at.localeCompare(b.created_at));

  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const mentionSuggestions: MentionSuggestion[] = allUsers.map((u) => ({
    id: u.id,
    name: u.full_name,
    role: u.role,
    is_ceo: u.is_ceo,
  }));

  const [draft, setDraft, clearDraft] = useCommentDraft(`comment-draft-subtask-${subtaskId}`);
  const [composerMentionIds, setComposerMentionIds] = useState<string[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [attachDragOver, setAttachDragOver] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editCommentContent, setEditCommentContent] = useState("");
  const [editCommentMentionIds, setEditCommentMentionIds] = useState<string[]>([]);
  const [showAssignPicker, setShowAssignPicker] = useState(false);
  const [assignPickerSearch, setAssignPickerSearch] = useState("");
  const [newChildTitle, setNewChildTitle] = useState("");
  const [openChildId, setOpenChildId] = useState<string | null>(null);
  const [rightTab, setRightTab] = useState<"comments" | "sheets">("comments");
  const { data: spreadsheetLinks = [], isLoading: linksLoading } = useSubTaskSpreadsheetLinks(taskId, subtaskId);
  const { mutateAsync: createLink, isPending: creatingLink } = useCreateSubTaskSpreadsheetLink(taskId, subtaskId);
  const { mutate: deleteLink } = useDeleteSubTaskSpreadsheetLink(taskId, subtaskId);
  const { mutateAsync: renameLink } = useRenameSubTaskSpreadsheetLink(taskId, subtaskId);
  const { mutateAsync: updateLink } = useUpdateSubTaskSpreadsheetLink(taskId, subtaskId);
  const attachFileRef = useRef<HTMLInputElement>(null);
  const attachDropZoneRef = useRef<HTMLDivElement>(null);
  const commentFileRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const [replyTo, setReplyTo] = useState<{ id: string; author_name: string; content: string } | null>(null);

  function handleReply(c: { id: string; author: string | null; author_name: string; content: string }) {
    if (!c.author) return;
    setReplyTo({ id: c.id, author_name: c.author_name, content: c.content });
  }

  function cancelReply() {
    setReplyTo(null);
  }

  const addFiles = useCallback((files: FileList | File[]) => {
    const allowed = Array.from(files).filter(isAcceptedFile);
    setPendingFiles((prev) => [...prev, ...allowed]);
  }, []);

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  useEffect(() => {
    const zone = dropZoneRef.current;
    if (!zone) return;
    let counter = 0;

    const onEnter = (e: DragEvent) => {
      if (!e.dataTransfer?.types.includes("Files")) return;
      e.preventDefault();
      counter += 1;
      setDragOver(true);
    };
    const onOver = (e: DragEvent) => {
      if (!e.dataTransfer?.types.includes("Files")) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
    };
    const onLeave = (e: DragEvent) => {
      e.preventDefault();
      counter -= 1;
      if (counter <= 0) { counter = 0; setDragOver(false); }
    };
    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      counter = 0;
      setDragOver(false);
      if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
    };

    zone.addEventListener("dragenter", onEnter);
    zone.addEventListener("dragover", onOver);
    zone.addEventListener("dragleave", onLeave);
    zone.addEventListener("drop", onDrop);

    const preventDefault = (e: DragEvent) => {
      if (e.dataTransfer?.types.includes("Files")) e.preventDefault();
    };
    window.addEventListener("dragover", preventDefault);
    window.addEventListener("drop", preventDefault);

    return () => {
      zone.removeEventListener("dragenter", onEnter);
      zone.removeEventListener("dragover", onOver);
      zone.removeEventListener("dragleave", onLeave);
      zone.removeEventListener("drop", onDrop);
      window.removeEventListener("dragover", preventDefault);
      window.removeEventListener("drop", preventDefault);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const zone = attachDropZoneRef.current;
    if (!zone) return;
    let counter = 0;

    const onEnter = (e: DragEvent) => {
      if (!e.dataTransfer?.types.includes("Files")) return;
      e.preventDefault();
      counter += 1;
      setAttachDragOver(true);
    };
    const onOver = (e: DragEvent) => {
      if (!e.dataTransfer?.types.includes("Files")) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
    };
    const onLeave = (e: DragEvent) => {
      e.preventDefault();
      counter -= 1;
      if (counter <= 0) { counter = 0; setAttachDragOver(false); }
    };
    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      counter = 0;
      setAttachDragOver(false);
      if (e.dataTransfer?.files?.length) {
        Array.from(e.dataTransfer.files).filter(isAcceptedFile).forEach((f) => uploadAttachment(f));
      }
    };

    zone.addEventListener("dragenter", onEnter);
    zone.addEventListener("dragover", onOver);
    zone.addEventListener("dragleave", onLeave);
    zone.addEventListener("drop", onDrop);

    return () => {
      zone.removeEventListener("dragenter", onEnter);
      zone.removeEventListener("dragover", onOver);
      zone.removeEventListener("dragleave", onLeave);
      zone.removeEventListener("drop", onDrop);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) return (
    <>
      <div className="fixed inset-0 z-[75] bg-black/30" onClick={onClose} />
      <div className="fixed inset-0 z-[80] flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    </>
  );
  if (!subtask) return null;

  const assignedIds = new Set(subtask.assignees.map((a) => a.id));

  function saveTitle() {
    const t = titleDraft.trim();
    if (t && t !== subtask!.title) updateSubTask({ id: subtaskId, data: { title: t } });
    setEditingTitle(false);
  }

  function handleDelete() {
    if (!window.confirm("Usunąć to podzadanie?")) return;
    deleteSubTask(subtaskId, { onSuccess: onClose });
  }

  function handleSend() {
    const content = draft.trim();
    if (!content) return;
    createComment(
      { content, mentionIds: composerMentionIds, replyTo: replyTo?.id },
      {
        onSuccess: (newComment) => {
          clearDraft();
          setComposerMentionIds([]);
          setReplyTo(null);
          if (pendingFiles.length > 0 && newComment) {
            const commentId = (newComment as { id: string }).id;
            for (const file of pendingFiles) {
              uploadCommentAttachment({ commentId, file });
            }
            setPendingFiles([]);
          }
        },
      }
    );
  }

  function submitEditComment() {
    if (!editingCommentId) return;
    updateComment(
      { id: editingCommentId, content: editCommentContent.trim(), mentionIds: editCommentMentionIds },
      { onSuccess: () => { setEditingCommentId(null); setEditCommentContent(""); setEditCommentMentionIds([]); } }
    );
  }

  function addAssignee(userId: string) {
    const newIds = [...Array.from(assignedIds), userId];
    updateSubTask({ id: subtaskId, data: { assignee_ids: newIds } });
    setShowAssignPicker(false);
  }

  function removeAssignee(userId: string) {
    const newIds = Array.from(assignedIds).filter((id) => id !== userId);
    updateSubTask({ id: subtaskId, data: { assignee_ids: newIds } });
  }

  return (
    <>
      <div className="fixed inset-0 z-[75] bg-black/30" onClick={onClose} />

      <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4">
        <div className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-5xl h-[92vh] sm:h-[88vh] flex flex-col overflow-hidden ">

          {/* Header */}
          <div className="flex items-start gap-4 px-6 py-4 border-b border-gray-100 flex-shrink-0">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  {subtask.task_project_name && (
                    <span className="text-[var(--color-primary)] font-medium">{subtask.task_project_name} · </span>
                  )}
                  <span>{subtask.task_title}</span>
                </p>
              </div>
              {editingTitle ? (
                <input
                  autoFocus
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  onBlur={saveTitle}
                  onKeyDown={(e) => e.key === "Enter" && saveTitle()}
                  className="w-full text-xl font-bold text-gray-900 dark:text-gray-100 border-b-2 border-[var(--color-primary)] outline-none bg-transparent"
                />
              ) : (
                <h2
                  className="text-xl font-bold text-gray-900 dark:text-gray-100 cursor-pointer hover:text-[var(--color-primary)] leading-snug"
                  onClick={() => { setTitleDraft(subtask.title); setEditingTitle(true); }}
                  title="Kliknij aby edytować"
                >
                  {subtask.title}
                </h2>
              )}
            </div>
            <div className="flex items-center gap-1 flex-shrink-0 mt-1">
              <button
                onClick={() => watchSubTask(!subtask.is_watching)}
                disabled={watchingSubTask}
                title={subtask.is_watching ? "Wypisz się z powiadomień" : "Subskrybuj powiadomienia"}
                className={`p-1.5 rounded-lg transition-colors ${
                  subtask.is_watching
                    ? "text-[var(--color-primary)] bg-[var(--color-primary-dim)] dark:bg-[var(--color-primary-hover)] hover:bg-[var(--color-primary-dim)]"
                    : "text-gray-400 dark:text-gray-500 hover:text-gray-600 hover:bg-gray-100"
                }`}
              >
                {subtask.is_watching ? <Bell size={16} className="fill-[var(--color-primary-dim)]" /> : <BellOff size={16} />}
              </button>
              <button onClick={onClose} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 p-1.5">
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Two-column body */}
          <div className="flex flex-col md:flex-row flex-1 overflow-hidden">

            {/* LEFT: details */}
            <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-6 border-r border-gray-100 min-w-0">

              {/* Status + Priority */}
              <div className="flex flex-wrap gap-4">
                <div className="flex flex-col gap-1.5">
                  <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Status</span>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <select
                        value={subtask.status}
                        onChange={(e) =>
                          updateSubTask({ id: subtaskId, data: { status: e.target.value as TaskStatus } })
                        }
                        className={`text-sm font-semibold px-4 py-2 rounded-lg border-0 cursor-pointer appearance-none pr-8 shadow-sm ${TASK_STATUS_COLORS[subtask.status]}`}
                      >
                        {STATUS_ORDER.map((s) => (
                          <option key={s} value={s}>{TASK_STATUS_LABELS[s]}</option>
                        ))}
                      </select>
                      <ChevronDown size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                    </div>
                    {subtask.done_by_cascade && (
                      <span
                        title="Ukończone automatycznie przez ukończenie podzadania nadrzędnego"
                        className="text-xs px-1.5 py-0.5 rounded-full bg-green-50 text-green-500 border border-green-200 dark:bg-green-950 dark:text-green-400 dark:border-green-800 cursor-default"
                      >
                        ↑ kaskada
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Priorytet</span>
                  <div className="relative">
                    <select
                      value={subtask.priority}
                      onChange={(e) =>
                        updateSubTask({ id: subtaskId, data: { priority: e.target.value as TaskPriority } })
                      }
                      className={`text-sm font-semibold px-4 py-2 rounded-lg border-0 cursor-pointer appearance-none pr-8 shadow-sm ${PRIORITY_COLORS[subtask.priority]}`}
                    >
                      {(Object.keys(PRIORITY_LABELS) as TaskPriority[]).map((p) => (
                        <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>
                      ))}
                    </select>
                    <ChevronDown size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>
                </div>
              </div>

              {/* Due date / week */}
              <div className="flex flex-col gap-2">
                <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Termin wykonania</span>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-800/50 px-3 py-2">
                    <Calendar size={15} className="text-gray-400 dark:text-gray-500 flex-shrink-0" />
                    <WeekSelect
                      value={subtask.due_date}
                      onChange={(date) =>
                        updateSubTask({ id: subtaskId, data: { due_date: date } })
                      }
                      placeholder="— nie ustawiono —"
                      className="text-sm text-gray-700 dark:text-gray-300 border-0 outline-none bg-transparent cursor-pointer"
                    />
                  </div>
                  <WeekBadge weeks={subtask.weeks_until_due} />
                </div>
              </div>

              {/* Assignees */}
              <div className="flex flex-col gap-2">
                <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Przypisani</span>
                <div className="flex items-center gap-2 flex-wrap">
                  {subtask.assignees.map((a) => (
                    <div
                      key={a.id}
                      className="flex items-center gap-1.5 bg-gray-100 dark:bg-gray-700/60 rounded-full pl-0.5 pr-2 py-0.5"
                    >
                      <UserAvatar
                        userId={a.id}
                        fullName={a.full_name}
                        profilePictureUrl={a.profile_picture_url}
                        size="sm"
                      />
                      <span className="text-xs text-gray-700 dark:text-gray-300 max-w-[120px] truncate">
                        {a.full_name}
                      </span>
                      {canUnassign && (
                        <button
                          onClick={() => removeAssignee(a.id)}
                          className="text-gray-400 hover:text-red-400 transition-colors"
                        >
                          <X size={11} />
                        </button>
                      )}
                    </div>
                  ))}
                  {subtask.assignees.length === 0 && (
                    <span className="text-sm text-gray-400 dark:text-gray-500 italic">Brak</span>
                  )}
                  <div className="relative">
                    <button
                      onClick={() => { setShowAssignPicker((v) => !v); setAssignPickerSearch(""); }}
                      className="flex items-center gap-1.5 text-sm text-[var(--color-primary)] hover:text-[var(--color-primary)] border border-[var(--color-primary)] rounded-full px-3 py-1.5 hover:bg-[var(--color-primary-dim)] transition-colors"
                    >
                      <UserPlus size={13} /> Dodaj
                    </button>
                    {showAssignPicker && (
                      <div className="absolute top-10 left-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl z-10 w-56 flex flex-col py-1.5">
                        <div className="px-2 pb-1.5">
                          <input
                            autoFocus
                            type="text"
                            placeholder="Szukaj..."
                            value={assignPickerSearch}
                            onChange={(e) => setAssignPickerSearch(e.target.value)}
                            className="w-full text-sm px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                          />
                        </div>
                        <div className="overflow-y-auto max-h-52">
                          {assignableUsers
                            .filter((u) => !assignedIds.has(u.id))
                            .filter((u) => u.full_name.toLowerCase().includes(assignPickerSearch.toLowerCase()))
                            .map((u) => (
                              <button
                                key={u.id}
                                onClick={() => addAssignee(u.id)}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-[var(--color-primary-dim)] flex items-center gap-2.5"
                              >
                                <UserAvatar
                                  fullName={u.full_name}
                                  profilePictureUrl={u.profile_picture_url}
                                  size="lg"
                                />
                                <div>
                                  <p className="font-medium text-gray-800 dark:text-gray-200 leading-tight">{u.full_name}</p>
                                  <p className="text-xs text-gray-400 dark:text-gray-500">{getRoleLabel(u.role, u.is_ceo)}</p>
                                </div>
                              </button>
                            ))}
                          {assignableUsers.filter((u) => !assignedIds.has(u.id)).filter((u) => u.full_name.toLowerCase().includes(assignPickerSearch.toLowerCase())).length === 0 && (
                            <p className="text-xs text-gray-400 dark:text-gray-500 px-3 py-2 italic">
                              {assignPickerSearch ? "Brak wyników" : "Wszyscy przypisani"}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Children subtasks */}
              <div className="flex flex-col gap-2">
                <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">
                  Podzadania{children.length > 0 && ` (${children.length})`}
                </span>
                {children.length > 0 && (
                  <ul className="space-y-0.5">
                    {children.map((child) => (
                      <li
                        key={child.id}
                        onClick={() => setOpenChildId(child.id)}
                        className="flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer border border-transparent hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:border-gray-200 transition-colors"
                      >
                        <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 font-medium ${TASK_STATUS_COLORS[child.status]}`}>
                          {TASK_STATUS_LABELS[child.status]}
                        </span>
                        {child.done_by_cascade && (
                          <span
                            title="Ukończone automatycznie przez ukończenie podzadania nadrzędnego"
                            className="text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 bg-green-50 text-green-500 border border-green-200 dark:bg-green-950 dark:text-green-400 dark:border-green-800 cursor-default"
                          >
                            ↑ kaskada
                          </span>
                        )}
                        <span className={`text-sm flex-1 truncate ${["done", "cancelled", "n_d"].includes(child.status) ? "line-through text-gray-400 dark:text-gray-500" : "text-gray-800 dark:text-gray-200"}`}>
                          {child.title}
                        </span>
                        {child.comments_count > 0 && (
                          <span className="flex items-center gap-0.5 text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                            <MessageSquare size={11} />
                            {child.comments_count}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                <div className="flex items-center gap-2">
                  <Plus size={13} className="text-gray-400 dark:text-gray-500 flex-shrink-0" />
                  <input
                    value={newChildTitle}
                    onChange={(e) => setNewChildTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        const title = newChildTitle.trim();
                        if (!title) return;
                        createChildSubTask(
                          { title, parent: subtaskId },
                          { onSuccess: () => setNewChildTitle("") }
                        );
                      }
                    }}
                    placeholder="Dodaj podzadanie..."
                    disabled={creatingChild}
                    className="flex-1 text-sm text-gray-700 dark:text-gray-300 placeholder-gray-400 bg-transparent outline-none border-b border-transparent focus:border-[var(--color-primary)] py-0.5"
                  />
                  {newChildTitle.trim() && (
                    <button
                      onClick={() => {
                        const title = newChildTitle.trim();
                        if (!title) return;
                        createChildSubTask(
                          { title, parent: subtaskId },
                          { onSuccess: () => setNewChildTitle("") }
                        );
                      }}
                      disabled={creatingChild}
                      className="text-xs text-[var(--color-primary)] hover:text-[var(--color-primary)] font-medium"
                    >
                      Dodaj
                    </button>
                  )}
                </div>
              </div>

              {/* Attachments */}
              <div className="flex flex-col gap-2">
                <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Załączniki</span>
                <div className="flex flex-col gap-1.5">
                  {attachments.map((att) => (
                    <div
                      key={att.id}
                      className="flex items-center gap-2 bg-gray-50 dark:bg-gray-800/50 border border-gray-100 rounded-lg px-3 py-2 group"
                    >
                      <span className="text-base">{fileIcon(att.original_name)}</span>
                      <a
                        href={att.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={att.original_name}
                        className="flex-1 text-sm text-[var(--color-primary)] hover:underline truncate"
                      >
                        {att.original_name}
                      </a>
                      <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">{att.uploaded_by_name}</span>
                      <button
                        onClick={() => deleteAttachment(att.id)}
                        className="text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                      >
                        <X size={13} />
                      </button>
                    </div>
                  ))}
                  {attachments.length === 0 && (
                    <p className="text-sm text-gray-400 dark:text-gray-500 italic">Brak załączników.</p>
                  )}
                  <div ref={attachDropZoneRef} className="inline-flex rounded-lg">
                    <input
                      ref={attachFileRef}
                      type="file"
                      accept={FILE_INPUT_ACCEPT}
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files) {
                          Array.from(e.target.files).filter(isAcceptedFile).forEach((f) => uploadAttachment(f));
                        }
                        e.target.value = "";
                      }}
                    />
                    <button
                      onClick={() => attachFileRef.current?.click()}
                      disabled={uploading}
                      className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg border border-dashed transition-colors disabled:opacity-40 ${attachDragOver ? "border-[var(--color-primary)] bg-[var(--color-primary-dim)] text-[var(--color-primary)]" : "border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]"}`}
                    >
                      {uploading ? <Loader2 size={13} className="animate-spin flex-shrink-0" /> : <Paperclip size={13} className="flex-shrink-0" />}
                      <span className="whitespace-nowrap">{uploading ? "Przesyłanie…" : "Dodaj plik lub przeciągnij tutaj"}</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Meta */}
              <div className="pt-4 border-t border-gray-100">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Utworzono</span>
                    <p className="mt-1 text-gray-600 dark:text-gray-400">{formatDateTime(subtask.created_at)}</p>
                  </div>
                  <div>
                    <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Ostatnia zmiana</span>
                    <p className="mt-1 text-gray-600 dark:text-gray-400">{formatDateTime(subtask.updated_at)}</p>
                  </div>
                </div>
              </div>

              {/* Director-only: linked planned revenues */}
              {isDirector && (
                <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
                  <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">
                    Planowane przychody
                  </span>
                  {linkedRevenues.length === 0 ? (
                    <p className="mt-1 text-xs text-gray-400 dark:text-gray-500 italic">
                      To zadanie nie blokuje żadnych planowanych przychodów.
                    </p>
                  ) : (
                    <div className="mt-2 space-y-1.5">
                      {linkedRevenues.map((r) => (
                        <div
                          key={r.id}
                          className="flex items-center justify-between gap-2 bg-gray-50 dark:bg-gray-800 rounded-lg px-3 py-2"
                        >
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              {r.is_blocked ? (
                                <Lock size={11} className="text-amber-500 flex-shrink-0" />
                              ) : (
                                <FileText size={11} className="text-green-500 flex-shrink-0" />
                              )}
                              <span className="text-xs text-gray-800 dark:text-gray-200 font-medium truncate">
                                {r.description}
                              </span>
                            </div>
                            <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
                              {Number(r.amount).toLocaleString("pl-PL", { useGrouping: "always" })} PLN · {formatDate(r.expected_date)}
                            </p>
                          </div>
                          {r.draft_invoice_id && canViewInvoicePrz && (
                            <Link
                              to={`/finances/invoices-prz/${r.draft_invoice_id}`}
                              className="text-[11px] text-[var(--color-primary)] hover:underline whitespace-nowrap flex-shrink-0"
                              title="Otwórz szkic faktury"
                            >
                              FV →
                            </Link>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div>
                <button
                  onClick={handleDelete}
                  className="flex items-center gap-1.5 text-sm text-red-400 hover:text-red-600 transition-colors"
                >
                  <Trash2 size={14} /> Usuń podzadanie
                </button>
              </div>
            </div>

            {/* RIGHT: comments / sheets */}
            <div className="w-full md:w-96 flex-shrink-0 flex flex-col overflow-hidden bg-gray-50 dark:bg-gray-800/50 border-t md:border-t-0 border-gray-100">
              <div className="px-5 pt-3 pb-0 border-b border-gray-100 flex-shrink-0">
                <div className="flex gap-4">
                  {(["comments", "sheets"] as const).map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setRightTab(tab)}
                      className={`flex items-center gap-1.5 text-sm pb-3 font-medium transition-colors ${
                        rightTab === tab
                          ? "border-b-2 border-[var(--color-primary)] text-[var(--color-primary)]"
                          : "text-gray-500 dark:text-gray-400 hover:text-gray-800"
                      }`}
                    >
                      {tab === "comments" ? (
                        <>
                          <MessageSquare size={13} />
                          Komentarze
                          {comments.length > 0 && (
                            <span className="text-xs font-normal opacity-60">({comments.length})</span>
                          )}
                        </>
                      ) : (
                        <>
                          <Table2 size={13} />
                          Arkusze
                          {spreadsheetLinks.length > 0 && (
                            <span className="text-xs font-normal opacity-60">({spreadsheetLinks.length})</span>
                          )}
                        </>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Sheets tab */}
              {rightTab === "sheets" && (
                <div className="flex-1 overflow-y-auto px-4 py-4">
                  <SpreadsheetLinkList
                    links={spreadsheetLinks}
                    isLoading={linksLoading}
                    onCreate={createLink}
                    onDelete={deleteLink}
                    onRename={(id, name) => renameLink({ linkId: id, name })}
                    onUpdate={(id, data) => updateLink({ linkId: id, data })}
                    isCreating={creatingLink}
                    taskId={taskId}
                    subtaskId={subtaskId}
                  />
                </div>
              )}

              {/* Comments tab content */}
              {rightTab === "comments" && <>
              <div className="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-1">
                {comments.length === 0 && (
                  <p className="text-sm text-gray-400 dark:text-gray-500 italic text-center py-10">Brak komentarzy.</p>
                )}
                {buildCommentTree(comments).map((node) => (
                  <CommentNode
                    key={node.id}
                    node={node}
                    depth={0}
                    currentUserId={currentUser?.id ?? null}
                    taskId={taskId}
                    subtaskId={subtaskId}
                    editingCommentId={editingCommentId}
                    editCommentContent={editCommentContent}
                    editCommentMentionIds={editCommentMentionIds}
                    mentionSuggestions={mentionSuggestions}
                    onEditStart={(id, content, mentionIds) => { setEditingCommentId(id); setEditCommentContent(content); setEditCommentMentionIds(mentionIds); }}
                    onEditCancel={() => { setEditingCommentId(null); setEditCommentContent(""); setEditCommentMentionIds([]); }}
                    onEditSave={submitEditComment}
                    onEditContentChange={setEditCommentContent}
                    onEditMentionIdsChange={setEditCommentMentionIds}
                    onDelete={(id) => deleteComment(id)}
                    onReply={(c) => handleReply(c)}
                    onDeleteAttachment={(commentId, attId) => deleteCommentAttachmentMutate({ commentId, attachmentId: attId })}
                  />
                ))}
              </div>

              {/* Composer */}
              <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex-shrink-0 bg-white dark:bg-gray-800">
                {replyTo && (
                  <div className="flex items-start gap-2 mb-2 bg-gray-100 dark:bg-gray-700 border-l-2 border-gray-400 dark:border-gray-500 rounded-r-lg px-3 py-1.5">
                    <CornerDownLeft size={12} className="text-gray-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">{replyTo.author_name}</span>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{stripHtml(replyTo.content)}</p>
                    </div>
                    <button onClick={cancelReply} className="text-gray-400 hover:text-red-400 flex-shrink-0">
                      <X size={12} />
                    </button>
                  </div>
                )}
                <RichTextEditor
                  value={draft}
                  onChange={setDraft}
                  disabled={sending}
                  placeholder="Napisz komentarz… (wpisz @ aby oznaczyć)"
                  onImageUpload={(file) => subtaskCommentsApi.uploadCommentImage(taskId, subtaskId, file)}
                  mentionSuggestions={mentionSuggestions}
                  onMentionIdsChange={setComposerMentionIds}
                />

                {pendingFiles.length > 0 && (
                  <ul className="flex flex-wrap gap-1.5 mt-2">
                    {pendingFiles.map((f, i) => (
                      <li key={i} className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded px-2 py-0.5 text-xs text-gray-700 dark:text-gray-300">
                        <span className="text-xs">{fileIcon(f.name)}</span>
                        <button
                          type="button"
                          onClick={() => openPendingFile(f)}
                          className="max-w-[130px] truncate hover:underline hover:text-[var(--color-primary)]"
                          title={`${f.name} — kliknij, aby podejrzeć`}
                        >
                          {f.name}
                        </button>
                        <button
                          onClick={() => setPendingFiles((prev) => prev.filter((_, j) => j !== i))}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <X size={10} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="flex items-center justify-between mt-2">
                  <div ref={dropZoneRef} className="inline-flex rounded-lg">
                    <input
                      ref={commentFileRef}
                      type="file"
                      accept={FILE_INPUT_ACCEPT}
                      multiple
                      className="hidden"
                      onChange={(e) => { if (e.target.files) addFiles(e.target.files); e.target.value = ""; }}
                    />
                    <button
                      onClick={() => commentFileRef.current?.click()}
                      disabled={sending}
                      className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg border border-dashed transition-colors disabled:opacity-40 ${dragOver ? "border-[var(--color-primary)] bg-[var(--color-primary-dim)] text-[var(--color-primary)]" : "border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]"}`}
                    >
                      <Paperclip size={13} className="flex-shrink-0" />
                      <span className="whitespace-nowrap">Załącz plik lub przeciągnij tutaj</span>
                    </button>
                  </div>
                  <button
                    onClick={handleSend}
                    disabled={!draft.trim() || sending}
                    className="p-2 bg-[var(--color-primary)] text-white rounded-xl hover:bg-[var(--color-primary-hover)] disabled:opacity-40 flex-shrink-0"
                  >
                    {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                  </button>
                </div>
              </div>
              </>}
            </div>
          </div>
        </div>
      </div>
      {/* Nested panel for child subtask */}
      {openChildId && (
        <SubTaskDetailPanel
          taskId={taskId}
          subtaskId={openChildId}
          onClose={() => setOpenChildId(null)}
        />
      )}
    </>
  );
}
